/**
 * 文件定位：
 * 1. 当前文件位于 web-client/views/editor；
 * 2. 负责子节点版本切换前的确认分发；
 * 3. 当切换会清理 Mate 时复用设计树删除确认弹框展示影响项，避免 Editor.vue 中散落弹框拼装逻辑。
 */

import {
  requestFeatureTreeDeleteConfirmation,
  type FeatureTreeDeleteConfirmItem,
  type FeatureTreeDeleteConfirmationDialogPort,
} from '@/views/editor/requestFeatureTreeDeleteConfirmation';

/**
 * 子节点版本切换确认输入。
 */
export interface RequestAssemblyChildNodeVersionSwitchConfirmationInput {
  /** NaiveUI dialog 实例。 */
  readonly dialog: FeatureTreeDeleteConfirmationDialogPort;
  /** 原有版本切换确认入口。 */
  readonly confirmVersionSwitch: () => Promise<boolean>;
  /** 即将切换的设计树节点。 */
  readonly target: FeatureTreeDeleteConfirmItem;
  /** 切换旧子树时会被同步清理的关联项。 */
  readonly relatedItems?: readonly FeatureTreeDeleteConfirmItem[];
}

/**
 * 请求子节点版本切换确认。
 *
 * 设计说明：
 * 子节点切换在语义上会先移除旧子树，再加载新子树。旧子树关联 Mate 会同步清理时，
 * 必须像删除一样展示具体影响项；没有 Mate 影响时保留原版本切换确认，避免普通切换增加额外干扰。
 *
 * @param input 切换确认输入。
 * @returns 用户是否确认继续切换。
 */
export function requestAssemblyChildNodeVersionSwitchConfirmation(
  input: RequestAssemblyChildNodeVersionSwitchConfirmationInput,
): Promise<boolean> {
  const relatedItems = input.relatedItems ?? [];
  if (relatedItems.length === 0) {
    return input.confirmVersionSwitch();
  }

  return requestFeatureTreeDeleteConfirmation({
    dialog: input.dialog,
    target: input.target,
    relatedItems,
    title: '确认切换',
    targetPrompt: '切换以下项目？',
    relatedItemsPrompt: '切换时将同时删除以下从属项目：',
  });
}
